import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Heading from "../Common/Heading";
import API, { action, downloadBlob } from "../../Api";
import { useLoader } from "../../LoaderContext";

const todayStr = () => new Date().toISOString().slice(0, 10);
const defaultPlanMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function apiErrorMessage(err, fallback) {
  const m = err?.response?.data?.response?.message ?? err?.response?.data?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

function emptyEditForm() {
  return {
    PR: "",
    MR: "",
    GR: "",
    PRg: "",
    early_lactation: "",
    transition_concentrate: "",
    suggested_silage_kg: "",
    suggested_straw_kg: "",
    suggested_green_grass_kg: "",
    reason: "",
  };
}

function formFromCalc(calc) {
  const c = calc?.final?.components_kg || {};
  const r = calc?.final?.roughage || {};
  return {
    PR: c.PR ?? "",
    MR: c.MR ?? "",
    GR: c.GR ?? "",
    PRg: c.PRg ?? "",
    early_lactation: c.early_lactation ?? "",
    transition_concentrate: c.transition_concentrate ?? "",
    suggested_silage_kg: r.suggested_silage_kg ?? "",
    suggested_straw_kg: r.suggested_straw_kg ?? "",
    suggested_green_grass_kg: r.suggested_green_grass_kg ?? "",
    reason: calc?.override?.reason ?? "",
  };
}

function parsePayload(form) {
  const keys = [
    "PR",
    "MR",
    "GR",
    "PRg",
    "early_lactation",
    "transition_concentrate",
    "suggested_silage_kg",
    "suggested_straw_kg",
    "suggested_green_grass_kg",
  ];
  const payload = {};
  for (const k of keys) {
    const raw = form[k];
    if (raw === "" || raw == null) {
      return { error: `Please enter a value for ${k.replace(/_/g, " ")}` };
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      return { error: `Invalid number: ${k}` };
    }
    payload[k] = n;
  }
  return { payload };
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function blobDownloadErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder().decode(data);
      const parsed = JSON.parse(text);
      const m = parsed?.response?.message ?? parsed?.message;
      if (typeof m === "string" && m.trim()) return m;
    } catch {
      /* not JSON */
    }
  }
  return apiErrorMessage(err, fallback);
}

function isSpreadsheetBlob(blob) {
  return (
    blob.type.includes("spreadsheetml") ||
    blob.type.includes("octet-stream") ||
    blob.type.includes("excel")
  );
}

function normalizePrivilegeIds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      if (p != null && typeof p === "object") {
        return Number(p.module_id ?? p.id ?? p.privilegeId ?? NaN);
      }
      return Number(p);
    })
    .filter((n) => !Number.isNaN(n));
}

/** User Management module in this app — used as admin-level access for global feeding rules. */
function hasFeedingRulesAdminAccess() {
  try {
    const ids = normalizePrivilegeIds(JSON.parse(localStorage.getItem("modulePrivileges") || "null") || []);
    return ids.includes(10);
  } catch {
    return false;
  }
}

const RULE_FORM_KEYS = [
  "pr_grams_per_liter",
  "early_lactation_max_kg",
  "pregnancy_additional_kg",
  "pregnancy_from_month",
  "roughage_bw_fraction",
  "dm_green_pct",
  "dm_silage_pct",
  "dm_straw_pct",
  "replacement_silage_dm_fraction",
  "replacement_straw_dm_fraction",
  "transition_concentrate_kg",
];

function emptyRuleForm() {
  const o = {};
  for (const k of RULE_FORM_KEYS) o[k] = "";
  return o;
}

function configToRuleForm(data) {
  const d = data || {};
  const o = {};
  for (const k of RULE_FORM_KEYS) {
    const v = d[k];
    o[k] = v === undefined || v === null ? "" : String(v);
  }
  return o;
}

function FeedingManagement() {
  const { setLoading } = useLoader();
  const [mode, setMode] = useState("planning");
  const [referenceDate, setReferenceDate] = useState(todayStr());
  const [planMonth, setPlanMonth] = useState(defaultPlanMonth());
  const [error, setError] = useState("");
  const [herdData, setHerdData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [cattleInternalId, setCattleInternalId] = useState("");
  const [singleResult, setSingleResult] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCattleId, setEditCattleId] = useState(null);
  const [editCalc, setEditCalc] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyEditForm());
  const [editModalError, setEditModalError] = useState("");

  const isRulesAdmin = useMemo(() => hasFeedingRulesAdminAccess(), []);
  const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState(() => emptyRuleForm());
  const [rulesLocked, setRulesLocked] = useState(false);
  const [rulesLoadError, setRulesLoadError] = useState("");
  const [rulesPanelError, setRulesPanelError] = useState("");
  const [rulesSaveSuccess, setRulesSaveSuccess] = useState("");
  const [rulesBootLoading, setRulesBootLoading] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [unlockForce, setUnlockForce] = useState(false);

  useEffect(() => {
    if (!rulesDrawerOpen || !isRulesAdmin) return undefined;
    let cancelled = false;
    setRulesLoadError("");
    setRulesPanelError("");
    setRulesSaveSuccess("");
    setRulesBootLoading(true);
    (async () => {
      try {
        const result = await action(API.FEEDING_CONFIG_GET, {});
        if (cancelled) return;
        const d = result?.response?.data ?? {};
        setRuleForm(configToRuleForm(d));
        setRulesLocked(!!d.rules_locked);
        setUnlockForce(false);
      } catch (e) {
        if (!cancelled) setRulesLoadError(apiErrorMessage(e, "Could not load feeding rules."));
      } finally {
        if (!cancelled) setRulesBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rulesDrawerOpen, isRulesAdmin]);

  const closeRulesDrawer = () => {
    setRulesDrawerOpen(false);
    setRulesLoadError("");
    setRulesPanelError("");
    setRulesSaveSuccess("");
    setUnlockForce(false);
  };

  const saveRulesConfig = async () => {
    setRulesPanelError("");
    setRulesSaveSuccess("");
    const body = {};
    for (const k of RULE_FORM_KEYS) {
      const n = Number(ruleForm[k]);
      if (!Number.isFinite(n)) {
        setRulesPanelError(`Invalid number for ${k.replace(/_/g, " ")}`);
        return;
      }
      body[k] = n;
    }
    if (rulesLocked && unlockForce) {
      body.force_unlock = true;
      body.rules_locked = false;
    }
    setRulesSaving(true);
    try {
      const result = await action(API.FEEDING_CONFIG_UPDATE, body);
      const d = result?.response?.data ?? {};
      setRuleForm(configToRuleForm(d));
      setRulesLocked(!!d.rules_locked);
      setUnlockForce(false);
      const okMsg =
        (typeof result?.response?.message === "string" && result.response.message.trim()) ||
        "Feeding rules saved.";
      setRulesSaveSuccess(okMsg);
      toast.success(okMsg, {
        position: "top-center",
        autoClose: 2800,
        hideProgressBar: true,
      });
    } catch (e) {
      setRulesPanelError(apiErrorMessage(e, "Could not save feeding rules."));
    } finally {
      setRulesSaving(false);
    }
  };

  const planParts = useMemo(() => {
    const [y, m] = planMonth.split("-");
    return { plan_year: Number(y), plan_month: Number(m) };
  }, [planMonth]);

  const herdRequestBody = useMemo(() => {
    if (mode === "planning") {
      return { mode: "planning", ...planParts, reference_date: referenceDate };
    }
    return { mode: "operational", reference_date: referenceDate };
  }, [mode, planParts, referenceDate]);

  const cattleRequestBody = (lookup) => {
    const q = String(lookup).trim();
    if (mode === "planning") {
      return { cattle_internal_id: q, mode: "planning", ...planParts, reference_date: referenceDate };
    }
    return { cattle_internal_id: q, mode: "operational", reference_date: referenceDate };
  };

  const loadHerd = async ({ cacheBust = false, keepPreviousOnError = false } = {}) => {
    setLoading(true);
    setError("");
    const body = cacheBust ? { ...herdRequestBody, _client_ts: Date.now() } : { ...herdRequestBody };
    try {
      const result = await action(API.FEEDING_HERD_SUMMARY, body);
      setHerdData(result?.response?.data ?? null);
      setForecastData(null);
      setSingleResult(null);
    } catch (e) {
      if (!keepPreviousOnError) {
        setHerdData(null);
      }
      setError("Could not load herd summary. Check API and login.");
    } finally {
      setLoading(false);
    }
  };

  const loadForecast = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await action(API.FEEDING_MONTHLY_FORECAST, {
        plan_year: planParts.plan_year,
        plan_month: planParts.plan_month,
      });
      setForecastData(result?.response?.data ?? null);
      setHerdData(null);
    } catch (e) {
      setForecastData(null);
      setError("Could not load monthly forecast.");
    } finally {
      setLoading(false);
    }
  };

  const loadSingle = async () => {
    if (!cattleInternalId.trim()) {
      setError("Enter database id or Cow ID tag.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await action(API.FEEDING_CALCULATE, cattleRequestBody(cattleInternalId));
      setSingleResult(result?.response?.data ?? null);
      setHerdData(null);
      setForecastData(null);
    } catch (e) {
      setSingleResult(null);
      setError(apiErrorMessage(e, "Could not calculate ration for this animal."));
    } finally {
      setLoading(false);
    }
  };

  const downloadHerdPdf = async () => {
    setError("");
    setLoading(true);
    try {
      const blob = await downloadBlob(API.FEEDING_REPORT_HERD_PDF, {
        ...herdRequestBody,
        _client_ts: Date.now(),
      });
      if (!blob.type.includes("pdf")) {
        setError("Herd PDF download failed (unexpected response).");
        return;
      }
      triggerDownload(blob, "feeding-herd-report.pdf");
    } catch (e) {
      setError("Could not download herd PDF.");
    } finally {
      setLoading(false);
    }
  };

  const downloadHerdExcel = async () => {
    setError("");
    setLoading(true);
    try {
      const blob = await downloadBlob(API.FEEDING_REPORT_HERD_EXCEL, {
        ...herdRequestBody,
        _client_ts: Date.now(),
      });
      if (!isSpreadsheetBlob(blob)) {
        setError("Herd Excel download failed (unexpected response).");
        return;
      }
      triggerDownload(blob, "feeding-herd-report.xlsx");
    } catch (e) {
      setError(await blobDownloadErrorMessage(e, "Could not download herd Excel."));
    } finally {
      setLoading(false);
    }
  };

  const downloadCattlePdf = async (internalId) => {
    setError("");
    setLoading(true);
    try {
      const blob = await downloadBlob(API.FEEDING_REPORT_CATTLE_PDF, {
        ...cattleRequestBody(internalId),
        _client_ts: Date.now(),
      });
      if (!blob.type.includes("pdf")) {
        setError("Cattle PDF download failed (unexpected response).");
        return;
      }
      triggerDownload(blob, `feeding-cattle-${internalId}.pdf`);
    } catch (e) {
      setError("Could not download cattle PDF.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCattleExcel = async (internalId) => {
    setError("");
    setLoading(true);
    try {
      const blob = await downloadBlob(API.FEEDING_REPORT_CATTLE_EXCEL, {
        ...cattleRequestBody(internalId),
        _client_ts: Date.now(),
      });
      if (!isSpreadsheetBlob(blob)) {
        setError("Cattle Excel download failed (unexpected response).");
        return;
      }
      triggerDownload(blob, `feeding-cattle-${internalId}.xlsx`);
    } catch (e) {
      setError(await blobDownloadErrorMessage(e, "Could not download cattle Excel."));
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (internalId) => {
    setEditModalError("");
    setEditCattleId(internalId);
    setEditModalOpen(true);
    setEditCalc(null);
    setEditForm(emptyEditForm());
    setLoading(true);
    try {
      const result = await action(API.FEEDING_CALCULATE, cattleRequestBody(internalId));
      const data = result?.response?.data ?? null;
      if (!data) {
        setEditModalError("Animal not found.");
        return;
      }
      setEditCalc(data);
      setEditForm(formFromCalc(data));
    } catch (e) {
      setEditModalError(apiErrorMessage(e, "Could not load ration for editing."));
    } finally {
      setLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditCattleId(null);
    setEditCalc(null);
    setEditForm(emptyEditForm());
    setEditModalError("");
  };

  const saveOverride = async () => {
    if (!editCattleId) return;
    const { payload, error: pe } = parsePayload(editForm);
    if (pe) {
      setEditModalError(pe);
      return;
    }
    setEditModalError("");
    setLoading(true);
    try {
      await action(API.FEEDING_OVERRIDE, {
        cattle_internal_id: Number(editCattleId),
        payload,
        reason: editForm.reason?.trim() || undefined,
      });
      await loadHerd({ cacheBust: true, keepPreviousOnError: true });
      closeEditModal();
    } catch (e) {
      setEditModalError("Could not save override.");
    } finally {
      setLoading(false);
    }
  };

  const removeOverride = async (internalId) => {
    if (!window.confirm("Remove manual feeding override for this animal?")) return;
    setLoading(true);
    setError("");
    try {
      await action(API.FEEDING_OVERRIDE_DELETE, { cattle_internal_id: Number(internalId) });
      if (editModalOpen && editCattleId === internalId) closeEditModal();
      await loadHerd({ cacheBust: true, keepPreviousOnError: true });
    } catch (e) {
      setError("Could not remove override.");
    } finally {
      setLoading(false);
    }
  };

  const animals = herdData?.animals ?? [];

  const inputClass =
    "mt-1 w-full min-w-0 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/25";
  const labelClass = "flex flex-col text-[10px] font-semibold uppercase tracking-wide text-slate-500";
  const btnSm =
    "inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-[#eef2f7] to-slate-100 py-3 px-3 sm:px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-3 flex w-full flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <span className="mt-1 hidden h-8 w-0.5 shrink-0 rounded-full bg-emerald-600 sm:block" aria-hidden />
            <div className="min-w-0">
              <Heading title="Feeding (Rations)" />
              <p className="mt-1 line-clamp-2 text-xs text-slate-500 sm:line-clamp-1">
                Herd summary, overrides, PDF/Excel · Planning = last 10 days prior month · Operational = last 7 days milk
              </p>
            </div>
          </div>
          {isRulesAdmin && (
            <button
              type="button"
              onClick={() => setRulesDrawerOpen(true)}
              className={`${btnSm} shrink-0 border border-slate-300 bg-white text-slate-800 shadow-sm hover:bg-slate-50 focus:ring-slate-400/40`}
            >
              Edit feeding rules
            </button>
          )}
        </header>

        <div className="flex flex-col gap-3">
          <section className="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm sm:p-4">
            <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end lg:gap-6">
              <div>
                <h2 className="text-xs font-semibold text-slate-800">Scenario &amp; dates</h2>
                <div className="mt-2 flex flex-wrap items-end gap-2 sm:gap-3">
                  <label className={labelClass}>
                    Mode
                    <select className={`${inputClass} min-w-[12rem] max-w-full sm:max-w-[20rem]`} value={mode} onChange={(e) => setMode(e.target.value)}>
                      <option value="planning">Planning (last 10 days of prev. month)</option>
                      <option value="operational">Operational (last 7 days milk)</option>
                    </select>
                  </label>
                  <label className={labelClass}>
                    Ref. date
                    <input type="date" className={`${inputClass} w-[9.5rem]`} value={referenceDate} onChange={(e) => setReferenceDate(e.target.value)} />
                  </label>
                  {mode === "planning" && (
                    <label className={labelClass}>
                      Plan month
                      <input type="month" className={`${inputClass} w-[9.5rem]`} value={planMonth} onChange={(e) => setPlanMonth(e.target.value)} />
                    </label>
                  )}
                  <div className="flex flex-wrap items-end gap-1.5 pb-0.5">
                    <button
                      type="button"
                      onClick={loadHerd}
                      className={`${btnSm} bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500`}
                    >
                      Load herd
                    </button>
                    <button
                      type="button"
                      onClick={downloadHerdPdf}
                      className={`${btnSm} border border-teal-700 bg-white text-teal-800 hover:bg-teal-50 focus:ring-teal-500/40`}
                    >
                      Herd PDF
                    </button>
                    <button
                      type="button"
                      onClick={downloadHerdExcel}
                      className={`${btnSm} border border-emerald-700 bg-white text-emerald-800 hover:bg-emerald-50 focus:ring-emerald-500/40`}
                    >
                      Herd Excel
                    </button>
                    <button
                      type="button"
                      onClick={loadForecast}
                      className={`${btnSm} border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 focus:ring-slate-400/30`}
                    >
                      Forecast
                    </button>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                <h2 className="text-xs font-semibold text-slate-800">Single animal</h2>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className={`${labelClass} shrink-0`}>
                    Cow ID or #
                    <input
                      type="text"
                      placeholder="e.g. 42 or tag"
                      className={`${inputClass} min-w-[7.5rem] w-36`}
                      value={cattleInternalId}
                      onChange={(e) => setCattleInternalId(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={loadSingle}
                    className={`${btnSm} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500`}
                  >
                    Calculate
                  </button>
                </div>
              </div>
            </div>
          </section>

          {error && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 shadow-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          {herdData && (
            <section className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-200/60">
              <div className="border-b border-slate-100 bg-slate-50/80 px-3 py-2 sm:px-4">
                <h2 className="text-sm font-semibold text-slate-900">Herd summary</h2>
                <p className="text-[10px] text-slate-500">Daily herd totals for this scenario</p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-2 sm:grid-cols-5 lg:gap-2">
                {[
                  { label: "Animals", value: herdData.count, accent: "text-slate-900" },
                  { label: "Conc. kg/d", value: herdData.totals_daily_kg?.concentrate, accent: "text-emerald-700" },
                  { label: "Silage kg/d", value: herdData.totals_daily_kg?.silage, accent: "text-slate-800" },
                  { label: "Straw kg/d", value: herdData.totals_daily_kg?.straw, accent: "text-slate-800" },
                  { label: "Green kg/d", value: herdData.totals_daily_kg?.green_grass, accent: "text-slate-800" },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-lg border border-slate-100 bg-white px-2 py-1.5 shadow-sm"
                  >
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{s.label}</p>
                    <p className={`text-base font-bold tabular-nums leading-tight ${s.accent}`}>{s.value ?? "—"}</p>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border-t border-slate-100">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-800 text-white shadow-md">
                    <tr>
                      {[
                        "Cow ID",
                        "Category",
                        "Weight",
                        "Conc.",
                        "PR",
                        "MR",
                        "GR",
                        "PRg",
                        "Early",
                        "Trn",
                        "Silage",
                        "Straw",
                        "Green",
                        "Milk",
                        "Ovr",
                        "Actions",
                      ].map((h) => (
                        <th key={h} className="whitespace-nowrap px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-200">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {animals.map((row) => (
                      <tr
                        key={row.cattle_internal_id}
                        className="transition hover:bg-emerald-50/40"
                      >
                        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-800">{row.cattle_id_display}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs text-slate-600">{row.category}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.context?.bodyWeightKg ?? "—"}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs font-medium tabular-nums text-slate-900">{row.concentrate_total_kg}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.PR}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.MR}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.GR}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.PRg}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.early_lactation}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.components_kg?.transition_concentrate}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.roughage?.suggested_silage_kg}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.roughage?.suggested_straw_kg}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.roughage?.suggested_green_grass_kg}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">{row.context?.avgLitersPerDay}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-slate-600">{row.has_override ? "✓" : "—"}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              className="rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
                              onClick={() => openEditModal(row.cattle_internal_id)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-teal-600 bg-white px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-50"
                              onClick={() => downloadCattlePdf(row.cattle_internal_id)}
                            >
                              PDF
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-emerald-600 bg-white px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
                              onClick={() => downloadCattleExcel(row.cattle_internal_id)}
                            >
                              Excel
                            </button>
                            {row.has_override && (
                              <button
                                type="button"
                                className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                                onClick={() => removeOverride(row.cattle_internal_id)}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {forecastData && (
            <section className="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm sm:p-4">
              <h2 className="text-xs font-semibold text-slate-800">Monthly procurement (forecast)</h2>
              <p className="mt-0.5 text-[10px] text-slate-500">
                {forecastData.planYear}-{String(forecastData.planMonth).padStart(2, "0")} · {forecastData.days_in_month} days ·{" "}
                {forecastData.milk_rule}
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ["Concentrate (month)", forecastData.totals_month_kg?.concentrate],
                  ["Silage", forecastData.totals_month_kg?.silage],
                  ["Straw", forecastData.totals_month_kg?.straw],
                  ["Green grass equiv.", forecastData.totals_month_kg?.green_grass],
                ].map(([lab, val]) => (
                  <div key={lab} className="rounded-lg border border-slate-100 bg-slate-50/80 px-2 py-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{lab}</p>
                    <p className="text-sm font-bold tabular-nums text-slate-900">
                      {val ?? "—"} <span className="text-[10px] font-normal text-slate-500">kg</span>
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {singleResult && (
            <section className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-200/60">
              <div className="border-b border-slate-100 bg-slate-50/80 px-3 py-2 sm:px-4">
                <h2 className="text-sm font-semibold text-slate-900">Single animal</h2>
                <p className="text-[10px] text-slate-500">
                  #{singleResult.context?.cattle_internal_id} · {singleResult.context?.cattle_id_display}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-800 text-white shadow-md">
                    <tr>
                      {[
                        "Cow ID",
                        "Category",
                        "Weight",
                        "Conc.",
                        "PR",
                        "MR",
                        "GR",
                        "PRg",
                        "Early",
                        "Trn",
                        "Silage",
                        "Straw",
                        "Green",
                        "Milk",
                        "Ovr",
                        "Actions",
                      ].map((h) => (
                        <th
                          key={h}
                          className="whitespace-nowrap px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    <tr className="transition hover:bg-blue-50/40">
                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-800">
                        {singleResult.context?.cattle_id_display}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs text-slate-600">{singleResult.final?.category}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.context?.bodyWeightKg ?? "—"}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs font-medium tabular-nums text-slate-900">
                        {singleResult.final?.concentrate_total_kg}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.PR}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.MR}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.GR}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.PRg}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.early_lactation}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.components_kg?.transition_concentrate}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.roughage?.suggested_silage_kg}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.roughage?.suggested_straw_kg}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.final?.roughage?.suggested_green_grass_kg}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs tabular-nums text-slate-600">
                        {singleResult.context?.avgLitersPerDay}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-center text-xs text-slate-600">
                        {singleResult.override ? "✓" : "—"}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
                            onClick={() => openEditModal(singleResult.context?.cattle_internal_id)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-teal-600 bg-white px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-50"
                            onClick={() => downloadCattlePdf(singleResult.context?.cattle_internal_id)}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className="rounded-md border border-emerald-600 bg-white px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
                            onClick={() => downloadCattleExcel(singleResult.context?.cattle_internal_id)}
                          >
                            Excel
                          </button>
                          {singleResult.override && (
                            <button
                              type="button"
                              className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                              onClick={() => removeOverride(singleResult.context?.cattle_internal_id)}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>

      {rulesDrawerOpen && isRulesAdmin && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[1100] cursor-default bg-slate-900/50 transition-opacity"
            aria-label="Close feeding rules panel"
            onClick={closeRulesDrawer}
          />
          <div
            className="fixed inset-y-0 right-0 z-[1110] flex h-full max-h-screen w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feeding-rules-drawer-title"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 bg-slate-900 px-4 py-4 text-white">
              <div className="min-w-0">
                <h2 id="feeding-rules-drawer-title" className="text-lg font-semibold tracking-tight">
                  Feeding rules
                </h2>
                <p className="mt-1 text-xs text-slate-300">
                  Global ration parameters (BRS module). Changes apply to new calculations for all users.
                </p>
                <p className="mt-2 text-[11px] font-medium text-amber-200/95">
                  Status: {rulesLocked ? "Locked (approved)" : "Unlocked — editable"}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-white/90 transition hover:bg-white/10"
                onClick={closeRulesDrawer}
                aria-label="Close"
              >
                <span className="text-xl leading-none">×</span>
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {rulesLoadError && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{rulesLoadError}</div>
              )}
              {rulesPanelError && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{rulesPanelError}</div>
              )}
              {rulesSaveSuccess && (
                <div
                  className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                  role="status"
                >
                  {rulesSaveSuccess}
                </div>
              )}
              {rulesBootLoading && !rulesLoadError ? (
                <p className="text-sm text-slate-500">Loading configuration…</p>
              ) : (
                <div className="space-y-3">
                  {[
                    ["pr_grams_per_liter", "PR — grams per liter of milk"],
                    ["early_lactation_max_kg", "Early lactation max (kg)"],
                    ["pregnancy_additional_kg", "Pregnancy additional concentrate (kg)"],
                    ["pregnancy_from_month", "Apply pregnancy add-on from month (of gestation)"],
                    ["roughage_bw_fraction", "Roughage as fraction of body weight (green equiv.)"],
                    ["dm_green_pct", "Dry matter % — green grass"],
                    ["dm_silage_pct", "Dry matter % — silage"],
                    ["dm_straw_pct", "Dry matter % — straw"],
                    ["replacement_silage_dm_fraction", "Replacement mix — silage DM fraction"],
                    ["replacement_straw_dm_fraction", "Replacement mix — straw DM fraction"],
                    ["transition_concentrate_kg", "Transition concentrate (kg)"],
                  ].map(([key, lab]) => (
                    <label key={key} className={labelClass}>
                      {lab}
                      <input
                        type="number"
                        step="any"
                        className={inputClass}
                        disabled={rulesBootLoading || rulesSaving}
                        value={ruleForm[key] ?? ""}
                        onChange={(e) => setRuleForm((f) => ({ ...f, [key]: e.target.value }))}
                      />
                    </label>
                  ))}

                  {rulesLocked && (
                    <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0 rounded border-amber-400"
                        checked={unlockForce}
                        disabled={rulesBootLoading || rulesSaving}
                        onChange={(e) => setUnlockForce(e.target.checked)}
                      />
                      <span>Unlock rules for this save (required while rules are locked).</span>
                    </label>
                  )}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap gap-2 border-t border-slate-100 bg-slate-50/90 px-4 py-3">
              <button
                type="button"
                onClick={closeRulesDrawer}
                disabled={rulesBootLoading || rulesSaving}
                className={`${btnSm} border border-slate-300 bg-white text-slate-800 hover:bg-slate-100 focus:ring-slate-400/40 disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRulesConfig}
                disabled={rulesBootLoading || rulesSaving || !!rulesLoadError}
                className={`${btnSm} bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500 disabled:opacity-50`}
              >
                {rulesSaving ? "Saving…" : "Save rules"}
              </button>
            </div>
          </div>
        </>
      )}

      {editModalOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feeding-edit-title"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-emerald-600 to-teal-700 px-5 py-4 text-white">
              <div>
                <h4 id="feeding-edit-title" className="text-lg font-semibold">
                  Edit feeding (override)
                </h4>
                <p className="mt-1 text-xs text-emerald-100">
                  Saved per animal only. All numeric fields are required.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-white/90 transition hover:bg-white/10"
                onClick={closeEditModal}
                aria-label="Close"
              >
                <span className="text-xl leading-none">×</span>
              </button>
            </div>
            <div className="max-h-[calc(90vh-8rem)] overflow-y-auto p-5">
              {editModalError && (
                <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{editModalError}</p>
              )}
              {!editCalc && !editModalError && <p className="text-sm text-slate-500">Loading…</p>}
              {editCalc && (
                <div className="space-y-4 text-sm">
                  <p className="rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">
                    ID {editCalc.context?.cattle_internal_id} · {editCalc.context?.cattle_id_display}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["PR (kg)", "PR"],
                      ["MR (kg)", "MR"],
                      ["GR (kg)", "GR"],
                      ["PRg (kg)", "PRg"],
                      ["Early lact. (kg)", "early_lactation"],
                      ["Transition (kg)", "transition_concentrate"],
                      ["Silage (kg)", "suggested_silage_kg"],
                      ["Straw (kg)", "suggested_straw_kg"],
                      ["Green grass (kg)", "suggested_green_grass_kg"],
                    ].map(([label, key]) => (
                      <label key={key} className="flex flex-col text-xs font-medium text-slate-600">
                        {label}
                        <input
                          type="number"
                          step="0.01"
                          className={inputClass}
                          value={editForm[key]}
                          onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))}
                        />
                      </label>
                    ))}
                  </div>
                  <label className="flex flex-col text-xs font-medium text-slate-600">
                    Reason (optional)
                    <input
                      type="text"
                      className={inputClass}
                      value={editForm.reason}
                      onChange={(e) => setEditForm((f) => ({ ...f, reason: e.target.value }))}
                    />
                  </label>
                  <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                      onClick={saveOverride}
                    >
                      Save override
                    </button>
                    {editCalc.override && (
                      <button
                        type="button"
                        className="rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                        onClick={() => removeOverride(editCattleId)}
                      >
                        Remove override
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-center"
        autoClose={2800}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
        style={{ zIndex: 12000 }}
      />
    </div>
  );
}

export default FeedingManagement;
